import { Box, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import React from "react";
import { useDispatch } from "react-redux";
import moment from "moment-timezone";

import { notificationAdd } from "../../actions/notification";
import { getClients } from "../../api/reports";
import { CalendarFunctions } from "../../components/Calendar";
import { convertADtoBS } from "../../components/Calendar/functions/calendarFunctions";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { tl } from "../../components/translate";
import { downloadCSV } from "../../helpers/files";
import { ageFormatter } from "../../helpers/formatters";
import { commonErrorMessage } from "../../helpers/messages";
import useMobileScreen from "../../hooks/useMobileScreen";
import { Client } from "../../interfaces/ClientInterface";
import { getClientGender } from "../Assessment/AssessmentClientSelect";
import { getGenderCode } from "../Billing/PrintBill/EightyMmPrint/ClientInfo";
import ClientCreateEdit from "../Client/ClientCreateEdit";
import ClientInfo from "../Client/ClientInfo/ClientInfo";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import Filters, { FilterProps, SHOW_CLIENT_BY } from "./Filters";
import {
  clientReportDefaultCols,
  clientReportHiddenCols,
  getDefaultKeyValuesColumns
} from "../../components/ListHeaderShowHideDialog/helpers";
import ListHeaderShowHideDialog from "../../components/ListHeaderShowHideDialog/ListHeaderShowHideDialog";
import { COLUMN_STORAGE_KEYS, useListColumnVisibility } from "../../hooks/useListColumnVisibility";

// @ts-expect-error - css
import "./Reports.scss";

const docColumns = [
  "Client Id",
  "Customer Number",
  "Client Name",
  "Age",
  "Gender",
  "Phone",
  "Email",
  "National Identity Number",
  "Address",
  "Last Visited Date",
  "knownUsFrom",
  "bloodGroup",
  "city",
  "ethnicity",
  "maritalStatus",
  "nationality",
  "occupation",
  "insuranceNumber",
  "internalNotes",
  "registrationNo"
];

// New client is client which is just created.
// Old client is client which has different day for created_at and lastVisited
enum ClientTypeValue {
  NEW = "New",
  OLD = "Old"
}
const getClientTypeValue = (createdAt: string, lastVisitedAt: string) => {
  if (!lastVisitedAt || moment(createdAt).isSame(moment(lastVisitedAt), "day")) {
    return ClientTypeValue.NEW;
  }
  return ClientTypeValue.OLD;
};

const docRowProcessor = ({
  id,
  customerNumber,
  firstName,
  lastName,
  dob,
  gender,
  phone,
  email,
  address,
  lastVisitedAt,
  knownUsFrom,
  bloodGroup,
  city,
  ethnicity,
  maritalStatus,
  nationality,
  occupation,
  insuranceNumber,
  internalNotes,
  nationalIdentityNumber,
  registrationNo
}: {
  id: number;
  customerNumber: number;
  firstName: string;
  lastName: string;
  dob: Date;
  gender: string;
  phone: number;
  email: string;
  address: string;
  lastVisitedAt: string;
  knownUsFrom: string;
  bloodGroup: string;
  city: string;
  ethnicity: string;
  maritalStatus: string;
  nationality: string;
  occupation: string;
  insuranceNumber: number;
  internalNotes: string;
  nationalIdentityNumber: number;
  registrationNo: number;
}) => [
  id,
  customerNumber,
  `${firstName} ${lastName}`,
  dob ? ageFormatter(dob) : "",
  getClientGender(gender),
  phone,
  email,
  nationalIdentityNumber,
  address,
  lastVisitedAt ? convertADtoBS(new Date(lastVisitedAt)).formatted3 : "",
  knownUsFrom,
  bloodGroup,
  city,
  ethnicity,
  maritalStatus,
  nationality,
  occupation,
  insuranceNumber,
  internalNotes,
  registrationNo
];

export default function ClientReport(): React.JSX.Element {
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();
  const { columns: listColumns, setColumns: setListColumns } = useListColumnVisibility(
    COLUMN_STORAGE_KEYS.CLIENT_REPORT_COLUMNS,
    getDefaultKeyValuesColumns(clientReportDefaultCols, clientReportHiddenCols)
  );

  const [filters, setFilters] = React.useState<FilterProps>({
    from: CalendarFunctions.startOfDay(new Date()).toDate(),
    until: new Date(),
    clientIds: [],
    showClientByDate: SHOW_CLIENT_BY.LAST_VISITED_DATE,
    knownUsFrom: ""
  });
  const [reportData, setReportData] = React.useState<Client[]>([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [isColumnDialogOpen, setIsColumnDialogOpen] = React.useState(false);
  const [selectedClient, setSelectedClient] = React.useState<Client>({} as Client);
  const [clientEditMode, setClientEditMode] = React.useState<boolean>(false);

  // helpers
  const handleViewClose = () => setSelectedClient({} as Client);

  // effects
  React.useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const res = await getClients(filters);
        setReportData(res);
      } catch (error) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            message: error?.message || commonErrorMessage,
            autoTimeout: true,
            variant: "error"
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [filters]);

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* filter */}
        <Box sx={{ px: 3, py: 2 }}>
          <Filters
            filters={filters}
            onSetFilters={(updatedFilters) => setFilters(updatedFilters)}
            isRequestLoading={isRequestLoading}
          />
        </Box>

        {/* list */}
        <Box
          className="clientReportList"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            width: "100%",
            overflow: "auto"
          }}
        >
          <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto", mb: "40px" }}>
            <List
              automation="clientReportList"
              data={reportData}
              rowHeight={50}
              defaultSortColumn="referrers"
              defaultSortOrder={-1}
              activeRow={1}
              adjustHeightToContents
              columns={[
                {
                  key: "id",
                  label: "Client Id",
                  sortable: true,
                  sortFunction: (a, b) => (a.id > b.id ? 1 : -1),
                  formatter: ({ id }) => <Typography>{id}</Typography>
                },
                {
                  key: "customerNumber",
                  label: "Customer No.",
                  sortable: true,
                  formatter: ({ customerNumber }) => <Typography>{customerNumber || ""}</Typography>
                },
                {
                  key: "clientName",
                  label: tl("name"),
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.clientName?.toLowerCase() > b.clientName?.toLowerCase() ? 1 : -1,
                  formatter: (row) => (
                    <Box
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        width: "150px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "flex"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClient(row);
                      }}
                    >
                      {`${row.firstName} ${row.lastName}`.toUpperCase()}
                    </Box>
                  )
                },
                {
                  key: "dob",
                  label: "DOB",
                  sortable: true,
                  sortFunction: (a, b) => (new Date(a.dob) > new Date(b.dob) ? 1 : -1),
                  formatter: ({ dob }) => (
                    <Typography justifyContent="center" alignItems="center">
                      {dob ? convertADtoBS(moment.utc(dob)).formatted4 : ""}
                    </Typography>
                  )
                },
                {
                  key: "age",
                  label: "Age",
                  sortable: true,
                  formatter: ({ dob }) => (
                    <Typography justifyContent="center" alignItems="center">
                      {dob ? ageFormatter(dob) : ""}
                    </Typography>
                  )
                },
                {
                  key: "gender",
                  label: "Gender",
                  sortable: true,
                  sortFunction: (a, b) => (a.gender > b.gender ? 1 : -1),
                  formatter: ({ gender }) => (
                    <Typography justifyContent="center" alignItems="center">
                      {getGenderCode(gender)}
                    </Typography>
                  )
                },
                {
                  key: "phone",
                  label: "Phone",
                  formatter: ({ phone }) => (
                    <Typography flex={1} flexDirection="column" alignItems="center">
                      {phone}
                    </Typography>
                  )
                },
                {
                  key: "email",
                  label: "Email",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.email?.toLowerCase() > b.email?.toLowerCase() ? 1 : -1,
                  formatter: ({ email }) => <Typography>{email}</Typography>
                },
                {
                  key: "knownUsFrom",
                  label: "Known Us From",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.knownUsFrom?.toLowerCase() > b.knownUsFrom?.toLowerCase() ? 1 : -1,
                  formatter: ({ knownUsFrom }) => <Typography>{knownUsFrom}</Typography>
                },
                {
                  key: "address",
                  label: "Address",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.address?.toLowerCase() > b.address?.toLowerCase() ? 1 : -1,
                  formatter: ({ address }) => <Typography>{address}</Typography>
                },
                {
                  key: "lastVisitedAt",
                  label: "Last Visited At",
                  sortable: true,
                  sortFunction: (a, b) =>
                    new Date(a.lastVisitedAt) > new Date(b.lastVisitedAt) ? 1 : -1,
                  formatter: ({ lastVisitedAt }) => (
                    <Typography>
                      {lastVisitedAt ? convertADtoBS(new Date(lastVisitedAt)).formatted3 : ""}
                    </Typography>
                  )
                },
                {
                  key: "bloodGroup",
                  label: "Blood Group",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.bloodGroup?.toLowerCase() > b.bloodGroup?.toLowerCase() ? 1 : -1,
                  formatter: ({ bloodGroup }) => <Typography>{bloodGroup}</Typography>
                },
                {
                  key: "city",
                  label: "City",
                  sortable: true,
                  sortFunction: (a, b) => (a.city?.toLowerCase() > b.city?.toLowerCase() ? 1 : -1),
                  formatter: ({ city }) => <Typography>{city}</Typography>
                },
                {
                  key: "ethnicity",
                  label: "Ethnicity",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.ethnicity?.toLowerCase() > b.ethnicity?.toLowerCase() ? 1 : -1,
                  formatter: ({ ethnicity }) => <Typography>{ethnicity}</Typography>
                },
                {
                  key: "maritalStatus",
                  label: "Marital Status",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.maritalStatus?.toLowerCase() > b.maritalStatus?.toLowerCase() ? 1 : -1,
                  formatter: ({ maritalStatus }) => <Typography>{maritalStatus}</Typography>
                },
                {
                  key: "nationality",
                  label: "Nationality",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.nationality?.toLowerCase() > b.nationality?.toLowerCase() ? 1 : -1,
                  formatter: ({ nationality }) => <Typography>{nationality}</Typography>
                },
                {
                  key: "occupation",
                  label: "Occupation",
                  sortable: true,
                  sortFunction: (a, b) =>
                    a.occupation?.toLowerCase() > b.occupation?.toLowerCase() ? 1 : -1,
                  formatter: ({ occupation }) => <Typography>{occupation}</Typography>
                },
                {
                  key: "registrationNo",
                  label: "Registration Number",
                  formatter: ({ registrationNo }) => <Typography>{registrationNo}</Typography>
                },
                {
                  key: "insuranceNumber",
                  label: "Insurance Number",
                  formatter: ({ insuranceNumber }) => <Typography>{insuranceNumber}</Typography>
                },
                {
                  key: "nationalIdentityNumber",
                  label: "National Identity Number",
                  formatter: ({ nationalIdentityNumber }) => (
                    <Typography>{nationalIdentityNumber || "N/A"}</Typography>
                  )
                },
                {
                  key: "internalNotes",
                  label: "Internal Notes",
                  formatter: ({ internalNotes }) => <Typography>{internalNotes}</Typography>
                },
                {
                  key: "clientType",
                  label: "Client Type",
                  sortable: true,
                  sortFunction: (a, b) =>
                    new Date(a.lastVisitedAt) > new Date(b.lastVisitedAt) ? 1 : -1,
                  formatter: ({ lastVisitedAt, createdAt }) => (
                    <Typography>
                      {filters.showClientByDate === SHOW_CLIENT_BY.CREATED_DATE
                        ? ClientTypeValue.NEW
                        : getClientTypeValue(createdAt, lastVisitedAt)}
                    </Typography>
                  )
                }
              ].filter((row) => listColumns[row.key])}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  No items to show.
                </Box>
              </EmptyView>
              <ListActions>
                {({ getProcessedData }) => (
                  <Menu>
                    <MenuItem
                      onClick={async () =>
                        downloadCSV(
                          `Client Report - ${convertADtoBS(filters.from).formatted}-${
                            convertADtoBS(filters.until).formatted
                          }`,
                          getProcessedData(),
                          docColumns,
                          (row) => docRowProcessor(row)
                        )
                      }
                    >
                      {tl("reports.excel")}
                    </MenuItem>
                    <MenuItem onClick={() => setIsColumnDialogOpen(true)}>
                      Show/Hide Columns
                    </MenuItem>
                  </Menu>
                )}
              </ListActions>
            </List>
          </Box>
          <Box className="totalBar">
            <Box
              sx={{
                display: "flex",
                px: 3,
                justifyContent: "space-between"
              }}
            >
              {tl("reports.totalClients")}
            </Box>
            <Typography>{reportData.length}</Typography>
          </Box>
        </Box>
      </Box>

      <ListHeaderShowHideDialog
        onChange={(updatedColumns) => setListColumns(updatedColumns)}
        requiredColumns={["id", "clientName"]}
        open={isColumnDialogOpen}
        onClose={() => setIsColumnDialogOpen(false)}
        columns={listColumns}
      />

      {!isEmpty(selectedClient) &&
        (clientEditMode ? (
          <ClientCreateEdit
            setEditMode={setClientEditMode}
            clientId={selectedClient.id}
            mode="edit"
            onCancel={() => {
              setClientEditMode(false);
            }}
            stayOnCurrentPage
          />
        ) : (
          <ClientInfo
            id={selectedClient.id}
            handleViewClose={handleViewClose}
            stayOnCurrentPage
            setEditMode={setClientEditMode}
          />
        ))}
    </>
  );
}
